import { useEffect, useState } from 'react';
import { Col, Row, Stack, SubHeading } from '@workramp/ui';
import _ from 'underscore';
import { ErrorPage } from '~/features/common';
import { MixPanel } from '~/common';
import { useDebouncedValue } from '~/hooks';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { getCurrentUser } from '~/common/explicit-exports';
import { useActionCable } from '~/providers';
import { createAIAnswer } from '../../api';
import { type OrderBy, type SearchResultItem, type SortBy } from '../../types';
import { useSearch } from '../../providers';
import { LearnerAIQuestionAnswer, StyledAdvancedSearchStack } from '../LearnerAIQuestionAnswer';
import { AdvancedSearchSortSelect } from '../AdvancedSearchSortSelect';
import { OrderByOptions, SortByOptions } from '../../constants';
import { AdvancedSearchFilters } from '../AdvancedSearchFilters/AdvancedSearchFilters';
import { AdvancedSearchMessageLayout } from '../AdvancedSearchMessageLayout';
import { AdvancedSearchResultsSkeleton } from '../AdvancedSearchResultsSkeleton';
import { SearchResults } from '../LearnerSearchResults';
import { AdvancedSearchLoadMore } from '../AdvancedSearchLoadMore/AdvancedSearchLoadMore';
import { LearnerSearchMixpanelEvents } from '../../constants/mixpanel';

const AI_ANSWERS_ENABLED = isFeatureEnabled('fy26_q3_ai_search_answers');

interface AdvancedSearchProps {
  hideTitle?: boolean;
  resultTarget?: '_self' | '_blank';
}

const sortResults = (results: SearchResultItem[], sortBy: SortBy, sortOrder: OrderBy) => {
  if (sortBy === SortByOptions.RELEVANCE) {
    return results;
  }

  // Sort results by updated time based on sortOrder (ascending or descending)
  const ascending = _.sortBy(results, 'updatedDate');
  return sortOrder === OrderByOptions.ASC ? ascending : ascending.reverse();
};

export const AdvancedSearch = ({ hideTitle, resultTarget }: AdvancedSearchProps) => {
  const { I18n } = window;
  const PAGE_TITLE = I18n.t('js.learner.advanced_search.page_title');
  const WELCOME_MSG_TITLE = I18n.t('js.learner.advanced_search.welcome_title');
  const WELCOME_MSG_SUBTITLE = I18n.t('js.learner.advanced_search.welcome_subtitle');
  const NO_RESULTS_MSG_TITLE = I18n.t('js.learner.advanced_search.no_results.title');
  const NO_RESULTS_MSG_SUBTITLE = I18n.t('js.learner.advanced_search.no_results.subtitle');

  const [answer, setAnswer] = useState(null);
  const [answerLoading, setAnswerLoading] = useState(false);
  const [answerContent, setAnswerContent] = useState([]);

  const userId = getCurrentUser()?.id;
  const consumer = useActionCable();

  const {
    isLoading,
    isError,
    searchResults,
    errorStatus,
    searchQuery,
    sortBy,
    sortOrder,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    areFiltersActive,
  } = useSearch();

  const renderContent = () => {
    // Only show the Welcome message when users visits the page with no predefined query
    // and noFilter are selected

    if (!searchQuery && !areFiltersActive) {
      return <AdvancedSearchMessageLayout title={WELCOME_MSG_TITLE} subtitle={WELCOME_MSG_SUBTITLE} />;
    }

    if (searchResults.length === 0) {
      return <AdvancedSearchMessageLayout title={NO_RESULTS_MSG_TITLE} subtitle={NO_RESULTS_MSG_SUBTITLE} />;
    }

    const sortedResults = sortResults(searchResults, sortBy, sortOrder);

    return (
      <>
        <SearchResults results={sortedResults} resultTarget={resultTarget} />
        {isFetchingNextPage && <AdvancedSearchResultsSkeleton numOfResults={1} />}
        {!isFetchingNextPage && hasNextPage && (
          <AdvancedSearchLoadMore
            onClick={() => {
              fetchNextPage();
              MixPanel.track(LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_LOAD_MORE);
            }}
          />
        )}
      </>
    );
  };

  const renderPage = () => {
    if (isLoading) {
      return <AdvancedSearchResultsSkeleton numOfResults={10} />;
    }

    if (isError) {
      return <ErrorPage status={errorStatus} />;
    }

    return renderContent();
  };

  // wait 1 full second before creating an AI Answer job to avoid costly spam
  const debouncedAIAnswerQuery = useDebouncedValue(searchQuery, 1000);

  useEffect(() => {
    if (!AI_ANSWERS_ENABLED) return;

    setAnswer(false);
    setAnswerContent([]);

    if (!userId || searchQuery?.length < 5) return;

    setAnswerLoading(true);
  }, [searchQuery, userId]);

  useEffect(() => {
    if (!AI_ANSWERS_ENABLED || !userId || debouncedAIAnswerQuery?.length < 5) return undefined;

    createAIAnswer(debouncedAIAnswerQuery);

    const subscription = consumer.subscriptions.create(
      {
        channel: 'LearnerSearchAnswers::QuestionAnswerChannel',
        user_id: userId,
      },
      {
        received: (resp) => {
          const { answer: newAnswer, answered, question: answeredQuestion, courses, resources } = resp;

          // we could be getting pinged with an answer to a different question than the one currently in the search bar, so skip that
          if (answeredQuestion !== debouncedAIAnswerQuery) return;

          setAnswerLoading(false);
          setAnswer(answered ? newAnswer : null);
          setAnswerContent([
            ...courses.map((c) => ({ ...c, content_type: 'guide' })),
            ...resources.map((r) => ({ ...r, content_type: 'resource' })),
          ]);
        },
      },
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, [debouncedAIAnswerQuery, userId, setAnswerLoading, setAnswer, consumer]);

  return (
    <Stack size={4} data-testid="advanced-search">
      {AI_ANSWERS_ENABLED && userId && (answerLoading || answer) && (
        <Row>
          <StyledAdvancedSearchStack size={2}>
            <LearnerAIQuestionAnswer
              answer={answer}
              loading={answerLoading}
              content={answerContent}
              variant="advanced"
            />
          </StyledAdvancedSearchStack>
        </Row>
      )}
      <Row justify="space-between" align="center">
        {!hideTitle && (
          <Col xs="auto">
            <SubHeading level="xl">{PAGE_TITLE}</SubHeading>
          </Col>
        )}
      </Row>
      <Row justify="space-between">
        <Col xs="auto">
          <AdvancedSearchFilters />
        </Col>
        <Col xs="auto">
          <AdvancedSearchSortSelect />
        </Col>
      </Row>
      {renderPage()}
    </Stack>
  );
};
